import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
} from "react";
import { Capability } from "@/types/Capability";
import { EventRoleDTO } from "@/types/EventRoleDTO";
import { Filter } from "@/types/Filter";
import { RoleSurfaceDefault } from "@/types/RoleSurfaceDefault";
import {
  computeLegacyFloor,
  resolveCapabilities,
} from "@/lib/authz/capabilities";

/**
 * Resolved per-event authorization view for the current user. Built once at
 * the EventLayoutShell boundary from the `roleQ` server fetch, then consumed
 * via `useCapabilities()` everywhere downstream.
 *
 * - `can(cap)` — the canonical fine-grained gate. Replaces the scattered
 *   `role === "X"` checks in new code; legacy spots stay on `useRole().role`
 *   until the P4 codemod.
 * - `legacyRoleFloor` — bridge for the legacy `role !== "VIEWER"` etc.
 *   checks. Never returns ADMIN/OWNER. Scanner-template-aware.
 * - `fieldAllowlist` / `rowFilters` — passed through from the server.
 *   FieldsProvider receives the allowlist; row filters are informational.
 * - `surfaceDefaults` — the role's per-list default view/filter, also a
 *   pass-through. Read it through `roleSurfaceDefault()`, which the lists call
 *   while seeding their initial view/filter.
 */
export interface CapabilitiesContextType {
  can: (cap: Capability) => boolean;
  canAny: (caps: Capability[]) => boolean;
  capabilities: ReadonlySet<Capability>;
  /** Raw role string from the backend, including "CUSTOM". */
  role: string;
  /** "EDITOR" or "VIEWER" — only meaningful for CUSTOM users; equals raw role otherwise. */
  legacyRoleFloor: "OWNER" | "ADMIN" | "EDITOR" | "VIEWER";
  templateId?: string;
  fieldAllowlist: string[] | null;
  rowFilters: Filter[];
  /**
   * Per-list default view/filter, already merged server-side: the
   * collaborator's own row wins over the role template, surface by surface.
   * ([] when neither carries one.)
   *
   * Entries written for THIS collaborator carry `setAt`; entries inherited from
   * the role template do not. `roleSurfaceDefault` reads that mark — there is
   * deliberately no list-level timestamp here, because one cannot say which of
   * the merged entries it describes.
   */
  surfaceDefaults: RoleSurfaceDefault[];
  isCustom: boolean;
  /**
   * The backend's organisational-send switch, from the self-fetched role
   * (`EventRoleDTO.organisationalSendEnabled`). `true` only when the server
   * said so — missing/false/unknown keeps "Organisational notice" hidden.
   */
  organisationalSendEnabled: boolean;
  /**
   * The backend's SMS channel switch (`EventRoleDTO.smsChannelEnabled`).
   * `true` only when the server said so — fail closed, like the
   * organisational switch above.
   */
  smsChannelEnabled: boolean;
  /**
   * The "fixed numbers" SMS recipient mode is open server-side
   * (`EventRoleDTO.smsFixedModeEnabled`). `true` only when the server said so.
   */
  smsFixedModeEnabled: boolean;
  /**
   * The role fetch is still in flight: `role` reads "VIEWER" only as the
   * fail-closed placeholder. Gates that would LOCK something for a lesser
   * role (the organisational-notice workflow save) wait instead of locking.
   */
  roleLoading: boolean;
}

const CapabilitiesContext = createContext<CapabilitiesContextType | undefined>(
  undefined,
);

export function CapabilitiesProvider({
  children,
  eventRole,
  roleLoading = false,
}: {
  children: ReactNode;
  /** The shell's role query has not landed yet (see `roleLoading`). */
  roleLoading?: boolean;
  /**
   * Self-fetched EventRoleDTO with the extended fields populated. When omitted
   * (older response paths, fail-closed defaults), the provider degrades to a
   * VIEWER-equivalent envelope with no capabilities.
   */
  eventRole?: EventRoleDTO;
}) {
  const value = useMemo<CapabilitiesContextType>(() => {
    // Use `||` (not `??`) so empty-string role (the EventLayoutShell loading
    // fallback at `{ role: "" }`) coerces to "VIEWER" instead of resolving to
    // an empty implicit-cap set. Without this, the sidebar paints empty during
    // the brief roleQ-loading window.
    const role = eventRole?.role || "VIEWER";
    const caps = resolveCapabilities({
      role,
      capabilities: eventRole?.capabilities,
    });
    const isCustom = role === "CUSTOM";
    const legacyRoleFloor = isCustom
      ? (eventRole?.legacyRoleFloor ?? computeLegacyFloor(caps))
      : (role === "OWNER" || role === "ADMIN" || role === "EDITOR" || role === "VIEWER"
          ? role
          : "VIEWER");
    return {
      can: (cap: Capability) => caps.has(cap),
      canAny: (list: Capability[]) => list.some((c) => caps.has(c)),
      capabilities: caps,
      role,
      legacyRoleFloor: legacyRoleFloor as CapabilitiesContextType["legacyRoleFloor"],
      templateId: eventRole?.templateId,
      fieldAllowlist: eventRole?.fieldAllowlist ?? null,
      rowFilters: eventRole?.rowFilters ?? [],
      surfaceDefaults: eventRole?.surfaceDefaults ?? [],
      isCustom,
      organisationalSendEnabled: eventRole?.organisationalSendEnabled === true,
      smsChannelEnabled: eventRole?.smsChannelEnabled === true,
      // Never wider than the channel itself, whatever a body claims.
      smsFixedModeEnabled:
        eventRole?.smsChannelEnabled === true && eventRole?.smsFixedModeEnabled === true,
      roleLoading,
    };
  }, [eventRole, roleLoading]);

  return (
    <CapabilitiesContext.Provider value={value}>
      {children}
    </CapabilitiesContext.Provider>
  );
}

export function useCapabilities(): CapabilitiesContextType {
  const ctx = useContext(CapabilitiesContext);
  if (!ctx) {
    throw new Error("useCapabilities must be used within a CapabilitiesProvider");
  }
  return ctx;
}

/**
 * Safe variant that returns a degraded fallback when no provider is present.
 * Used by components rendered outside the event scope (e.g. company-level
 * pages) so they don't crash when reading caps defensively.
 */
export function useCapabilitiesOptional(): CapabilitiesContextType {
  const ctx = useContext(CapabilitiesContext);
  return ctx ?? NO_CAPABILITIES;
}

/**
 * Module-scoped so the fallback keeps a STABLE identity: consumers put `can`
 * in memo/effect dependency arrays, and a fresh object every render would
 * recompute them forever.
 */
const NO_CAPABILITIES: CapabilitiesContextType = {
  can: () => false,
  canAny: () => false,
  capabilities: new Set(),
  role: "VIEWER",
  legacyRoleFloor: "VIEWER",
  templateId: undefined,
  fieldAllowlist: null,
  organisationalSendEnabled: false,
  smsChannelEnabled: false,
  smsFixedModeEnabled: false,
  roleLoading: false,
  rowFilters: [],
  surfaceDefaults: [],
  isCustom: false,
};
